import { useRef, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
  changePasswordRequestSchema,
  updateMeRequestSchema,
  type Locale,
  type UpdateMeRequest,
} from '@witty-board/shared'
import { useMeQuery } from '../api/auth.js'
import {
  useChangePasswordMutation,
  useUpdateMeMutation,
  useUploadAvatarMutation,
} from '../api/users.js'
import { ApiError } from '../api/client.js'
import { Avatar } from '../components/Avatar.js'
import { LoadingScreen } from '../components/LoadingScreen.js'

const LOCALES: readonly Locale[] = ['es', 'en']

const fieldClass =
  'rounded-card border border-border bg-surface-hi px-3 py-2 text-body text-text outline-none focus-visible:ring-2 focus-visible:ring-accent'

/** `changePasswordRequestSchema` + confirmación: solo de interfaz, no forma parte del contrato del API. */
const passwordFormSchema = changePasswordRequestSchema
  .extend({ confirm: z.string() })
  .refine((value) => value.new === value.confirm, {
    message: 'confirmMismatch',
    path: ['confirm'],
  })
type PasswordFormValues = z.infer<typeof passwordFormSchema>

/** Página de perfil (CLAUDE.md §6, fase 5): nombre, avatar, idioma, cambio de contraseña. */
export function ProfilePage() {
  const { t } = useTranslation()
  const me = useMeQuery()

  if (me.isPending) return <LoadingScreen />
  if (!me.data) return null

  return (
    <div className="mx-auto flex max-w-lg flex-col gap-8 px-6 py-6">
      <h1 className="text-title font-semibold text-text">{t('profile.title')}</h1>
      <AvatarSection name={me.data.name} avatarUrl={me.data.avatarUrl} />
      <ProfileForm name={me.data.name} locale={me.data.locale} />
      <PasswordForm />
    </div>
  )
}

function AvatarSection({ name, avatarUrl }: { name: string; avatarUrl: string | null }) {
  const { t } = useTranslation()
  const upload = useUploadAvatarMutation()
  const inputRef = useRef<HTMLInputElement>(null)

  function onFileChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (file) upload.mutate(file)
  }

  return (
    <section className="flex items-center gap-4">
      <Avatar name={name} avatarUrl={avatarUrl} size="md" />
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={upload.isPending}
          className="w-fit rounded-card bg-surface-hi px-3 py-2 text-body text-text hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
        >
          {upload.isPending ? t('profile.avatar.uploading') : t('profile.avatar.change')}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          onChange={onFileChange}
          className="hidden"
        />
        {upload.isError && (
          <p role="alert" className="text-meta text-danger">
            {upload.error instanceof ApiError ? upload.error.message : t('profile.avatar.error')}
          </p>
        )}
      </div>
    </section>
  )
}

function ProfileForm({ name, locale }: { name: string; locale: Locale }) {
  const { t } = useTranslation()
  const updateMe = useUpdateMeMutation()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UpdateMeRequest>({
    resolver: zodResolver(updateMeRequestSchema),
    defaultValues: { name, locale },
  })

  const onSubmit = handleSubmit((values) => updateMe.mutate(values))

  return (
    <form onSubmit={(event) => void onSubmit(event)} noValidate className="flex flex-col gap-4">
      <h2 className="text-body font-medium text-text">{t('profile.infoTitle')}</h2>

      <label className="flex flex-col gap-1">
        <span className="text-meta text-text-muted">{t('profile.nameLabel')}</span>
        <input
          aria-invalid={errors.name ? true : undefined}
          className={fieldClass}
          {...register('name')}
        />
        {errors.name && <span className="text-meta text-danger">{t('profile.nameRequired')}</span>}
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-meta text-text-muted">{t('profile.localeLabel')}</span>
        <select className={fieldClass} {...register('locale')}>
          {LOCALES.map((option) => (
            <option key={option} value={option}>
              {t(`profile.locales.${option}`)}
            </option>
          ))}
        </select>
      </label>

      {updateMe.isSuccess && <p className="text-meta text-text-muted">{t('profile.saved')}</p>}
      {updateMe.isError && <p className="text-meta text-danger">{t('profile.saveError')}</p>}

      <button
        type="submit"
        disabled={updateMe.isPending}
        className="w-fit rounded-card bg-accent px-4 py-2 text-body font-semibold text-text transition-colors duration-100 hover:bg-accent-hi focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
      >
        {t('common.save')}
      </button>
    </form>
  )
}

function PasswordForm() {
  const { t } = useTranslation()
  const [wrongCurrent, setWrongCurrent] = useState(false)
  const changePassword = useChangePasswordMutation()
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: { current: '', new: '', confirm: '' },
  })

  const onSubmit = handleSubmit(({ current, new: next }) => {
    setWrongCurrent(false)
    changePassword.mutate(
      { current, new: next },
      {
        onSuccess: () => reset({ current: '', new: '', confirm: '' }),
        onError: (error) =>
          setWrongCurrent(error instanceof ApiError && error.code === 'INVALID_CREDENTIALS'),
      },
    )
  })

  return (
    <form
      onSubmit={(event) => void onSubmit(event)}
      noValidate
      className="flex flex-col gap-4 border-t border-border pt-6"
    >
      <h2 className="text-body font-medium text-text">{t('profile.password.title')}</h2>

      <label className="flex flex-col gap-1">
        <span className="text-meta text-text-muted">{t('profile.password.currentLabel')}</span>
        <input type="password" className={fieldClass} {...register('current')} />
        {wrongCurrent && (
          <span className="text-meta text-danger">{t('profile.password.wrongCurrent')}</span>
        )}
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-meta text-text-muted">{t('profile.password.newLabel')}</span>
        <input type="password" className={fieldClass} {...register('new')} />
        {errors.new && (
          <span className="text-meta text-danger">{t('profile.password.newTooShort')}</span>
        )}
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-meta text-text-muted">{t('profile.password.confirmLabel')}</span>
        <input type="password" className={fieldClass} {...register('confirm')} />
        {errors.confirm && (
          <span className="text-meta text-danger">{t('profile.password.confirmMismatch')}</span>
        )}
      </label>

      {changePassword.isSuccess && (
        <p className="text-meta text-text-muted">{t('profile.password.saved')}</p>
      )}

      {/* Estilo secundario a propósito: el acento es el botón de acción
          principal de la pantalla (CLAUDE.md §6, "uno por pantalla") y esa
          plaza ya la ocupa el "Guardar" de los datos, arriba (ver
          docs/DECISIONS.md, fase 6). */}
      <button
        type="submit"
        disabled={changePassword.isPending}
        className="w-fit rounded-card bg-surface-hi px-4 py-2 text-body text-text hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
      >
        {t('common.save')}
      </button>
    </form>
  )
}
